import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import type { AppDispatch, RootState } from "../../../redux/store";

/* ⭐ SAME SLICES AS FreshStoreHome — one food feed, one basket, one till.
   The grocery counter simply scopes itself to Food › Grocery (0802). */
import {
  listenToPublishedFoods,
  filterLocalFoodsByCountry,
  setFilters,
  FoodItem,
} from "../../../redux/foodSlice";
import { addToCart, decrementCartItem, clearCart } from "../../../redux/cartSlice";
import {
  type RateType,
  DEFAULT_RATE_TYPE,
  calculateLineTotal,
} from "../../../redux/foodServiceFeeSlice";
import { listenToBrandsByBusinessType } from "../../../redux/commercePartnersSlice";
import { fetchAllProductSummaries } from "../../../redux/productRatingSlice";
import { CurrencyContext } from "../../../GLOBAL/CurrencyContext";

/* ⭐ BRAND + MARKETING ART — the logo and the two marketing images the
   store leans on. Nothing about them is defined here. */
import appLogo from "../../../assets/app/logo.png";
import { GroceryMarketing, Delivery } from "../../../CATEGORIES/UICategories";

/* ⭐ CATEGORY SOURCE — FreshStore saves `categoryId` as a
   CategoriesCollection id inside Food › Grocery, plus `attributes`
   keyed by attribute id. This page walks the same tree back up. */
import {
  AllCategoriesFlat,
  type CategoryItem,
} from "../../../CATEGORIES/CategoriesCollection";
import { attributesForCategory } from "../../../CATEGORIES/ATTRIBUTES/CategoriesCollectionAttributes";

import {
  Search, ShoppingBag, X, ChevronRight, ChevronLeft, Plus, Minus,
  User, ShoppingCart, MapPin, Globe, AlertTriangle, BadgePercent,
  Home, LayoutGrid, Check, ChevronDown, Clock, Package, Leaf,
  Snowflake, Sun, WheatOff, Flame, Sparkles, Tag, ShieldCheck, Truck,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   SCOPE — this storefront is the Grocery aisle of the Food
   department. Everything below drills the standard hierarchy:
   department → category → subCategory → productType → product.
═══════════════════════════════════════════════════════════ */
const FOOD_DEPT_ID = "08";
const GROCERY_BRANCH_ID = "0802";

/** Which commercePartners show up here. Flip if your seed data differs. */
const GROCERY_BUSINESS_TYPE = 9;

/** Marketing art, straight from UICategories — no hardcoded paths. */
const HERO_IMAGE = (GroceryMarketing?.[0] as any)?.image ?? "";
const COURIER_IMAGE = (Delivery?.[0] as any)?.image ?? (GroceryMarketing?.[1] as any)?.image ?? "";

/* ═══════════════════════════════════════════════════════════
   HEARTLAS GROCERY TOKENS
   The mark stays Heartlas red; the aisle is green. Red is kept
   for the logo, the search action and price cuts only — so a
   discount always reads as a discount.
═══════════════════════════════════════════════════════════ */
const G = {
  bg: "#FAF9F5",
  panel: "#FFFFFF",

  ink: "#151E12",
  inkSoft: "#5B6B57",
  inkFaint: "#93A08E",

  green: "#1E7A3C",
  greenDark: "#125C2C",
  greenDeep: "#0E4A24",
  greenSoft: "#E8F4EA",

  red: "#E01B24",
  redDark: "#B4111A",
  redSoft: "#FDEAEB",

  gold: "#F0A92B",
  goldSoft: "#FDF1DA",

  border: "#E9E7DF",
  divider: "#F2F0E9",
};

const GRAD_GREEN = `linear-gradient(135deg, ${G.green} 0%, ${G.greenDark} 100%)`;
const GRAD_HERO = `linear-gradient(100deg, ${G.greenDeep} 0%, ${G.greenDark} 46%, rgba(18,92,44,.35) 100%)`;

/* accent comes from the store's own brand, falling back to the aisle green */
const brandAccentColor = (partner: any): string =>
  partner?.brandColor || partner?.themeColor || partner?.color || G.green;

/* ═══════════════════════════════════════════════════════════
   CATEGORY TREE HELPERS — ancestry is ALWAYS resolved by walking
   parentId, never by string prefixes (PT-… / PRD-… break those).
═══════════════════════════════════════════════════════════ */
const CAT_MAP = new Map<string, CategoryItem>();
AllCategoriesFlat.forEach((c) => CAT_MAP.set(c.id, c));

const childrenOf = (parentId: string | null) =>
  AllCategoriesFlat.filter((c) => c.parentId === parentId);

const chainCache = new Map<string, string[]>();
const chainIdsOf = (id?: string | number | null): string[] => {
  if (id === null || id === undefined || id === "") return [];
  const key = String(id);
  const hit = chainCache.get(key);
  if (hit) return hit;
  const chain: string[] = [];
  const seen = new Set<string>();
  let cur = CAT_MAP.get(key);
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    chain.unshift(cur.id);
    cur = cur.parentId ? CAT_MAP.get(cur.parentId) : undefined;
  }
  chainCache.set(key, chain);
  return chain;
};

const catName = (id?: string | number | null) =>
  id === null || id === undefined ? "" : CAT_MAP.get(String(id))?.name ?? "";

const catPathLabel = (id?: string | number | null) =>
  chainIdsOf(id).map((cid) => catName(cid)).join(" › ");

/** Is this listing shelved in Food › Grocery? */
const isGrocery = (id?: string | number | null) =>
  chainIdsOf(id).includes(GROCERY_BRANCH_ID);

/* ═══════════════════════════════════════════════════════════
   ATTRIBUTES — resolved exactly the way the seller screen
   resolves them, so the defs line up with the saved keys.
═══════════════════════════════════════════════════════════ */
const attrCache = new Map<string, any[]>();
const resolveAttributes = (categoryId?: string | number | null): any[] => {
  if (categoryId === null || categoryId === undefined || categoryId === "") return [];
  const cacheKey = String(categoryId);
  const hit = attrCache.get(cacheKey);
  if (hit) return hit;

  const keys = [...chainIdsOf(categoryId)]
    .reverse()
    .flatMap((cid) => [`0${cid}`, cid]);

  let found: any[] = [];
  for (const key of keys) {
    try {
      const defs = attributesForCategory(key as any);
      if (defs && defs.length) { found = defs; break; }
    } catch {
      /* unknown key — keep walking up */
    }
  }
  attrCache.set(cacheKey, found);
  return found;
};

const resolveAttrValue = (attr: any, value: any): string => {
  if (value === undefined || value === null || value === "") return "";
  const label = (optId: any) => {
    const opt = attr.options?.find((o: any) => String(o.id) === String(optId));
    return opt ? (opt.name || opt.standard || String(optId)) : String(optId);
  };
  if (Array.isArray(value)) return value.map(label).join(", ");
  if (attr.options?.length) return label(value);
  return `${value}${attr.unit ? ` ${attr.unit}` : ""}`;
};

const isEmptyValue = (v: any) =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

/** Only the attributes the seller actually filled in. */
const savedAttrsOf = (item: any) => {
  const saved = item?.attributes ?? {};
  return resolveAttributes(item?.categoryId)
    .map((def: any) => ({ def, raw: saved[String(def.id)] }))
    .filter(({ raw }) => !isEmptyValue(raw));
};

/* ═══════════════════════════════════════════════════════════
   PACK SIZES / EXTRAS — FreshStore writes `portions` as an array
   of {id,name,price,unit,stock}; older docs used a keyed map.
   Both are accepted so nothing in the wild breaks.
═══════════════════════════════════════════════════════════ */
export interface Portion { id: string; name: string; price: number; unit?: string; stock?: number }
export interface Extra { id: string; name: string; price: number }

const portionsOf = (item: any): Portion[] => {
  const raw = item?.portions;
  if (!raw) return [];
  const list: Portion[] = Array.isArray(raw)
    ? raw.map((p: any) => ({
        id: String(p?.id ?? p?.name ?? ""),
        name: p?.name ?? String(p?.id ?? ""),
        price: Number(p?.rate ?? p?.price) || 0,
        unit: p?.unit ?? "",
        stock: Number(p?.stock) || 0,
      }))
    : Object.entries(raw).map(([k, v]: [string, any]) => ({
        id: k,
        name: v?.name ?? k,
        price: Number(v?.rate ?? v?.price) || 0,
        unit: v?.unit ?? "",
        stock: Number(v?.stock) || 0,
      }));
  return list.filter((p) => p.id && p.name);
};

const extrasOf = (item: any): Extra[] =>
  Array.isArray(item?.extras)
    ? item.extras
        .map((e: any) => ({ id: String(e?.id ?? e?.name ?? ""), name: e?.name ?? "", price: Number(e?.rate ?? e?.price) || 0 }))
        .filter((e: Extra) => e.id && e.name)
    : [];

const buildCartId = (item: any, sizeId: string | null, extraIds: string[]) => {
  if (!sizeId && extraIds.length === 0) return item.listingId;
  const extraPart = extraIds.length ? extraIds.slice().sort().join("+") : "none";
  return `${item.listingId}__${sizeId ?? "base"}__${extraPart}`;
};

const computeItemPrice = (item: any, sizeId: string | null, extraIds: string[]) => {
  const base = item?.rate ?? 0;
  const size = portionsOf(item).find((p) => p.id === sizeId);
  const extras = extrasOf(item);
  const extrasSum = extraIds.reduce((sum, id) => sum + (extras.find((e) => e.id === id)?.price || 0), 0);
  return base + (size?.price || 0) + extrasSum;
};

const buildCartLabel = (item: any, sizeId: string | null, extraIds: string[]) => {
  const size = portionsOf(item).find((p) => p.id === sizeId);
  const extras = extrasOf(item);
  let label = item?.name || "";
  if (size) label += ` (${size.name}${size.unit ? ` · ${size.unit}` : ""})`;
  const extraNames = extraIds.map((id) => extras.find((e) => e.id === id)?.name).filter(Boolean) as string[];
  if (extraNames.length) label += ` + ${extraNames.join(", ")}`;
  return label;
};

/* ═══════════════════════════════════════════════════════════
   SHELF HELPERS — how it's measured, how long it's got,
   how it's kept, and who can eat it.
═══════════════════════════════════════════════════════════ */
const measureLabel = (item: any) => {
  const n = item?.netContent;
  const u = item?.unit;
  if (n && u) return `${n} ${u}`;
  if (u) return `per ${u}`;
  return "";
};

const daysLeft = (bestBefore?: string): number | null => {
  if (!bestBefore) return null;
  const t = Date.parse(bestBefore);
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - Date.now()) / 86_400_000);
};

const freshnessChip = (item: any): { text: string; color: string } | null => {
  const d = daysLeft(item?.bestBefore);
  if (d === null) {
    const shelf = item?.shelfLifeDays;
    return shelf ? { text: `${shelf}-day shelf life`, color: G.green } : null;
  }
  if (d <= 0) return { text: "Use today", color: G.red };
  if (d === 1) return { text: "1 day left", color: G.red };
  if (d <= 3) return { text: `${d} days left`, color: G.gold };
  return { text: `Good for ${d} days`, color: G.green };
};

const STORAGE_META: Record<string, { label: string; icon: any }> = {
  frozen: { label: "Keep frozen", icon: Snowflake },
  chilled: { label: "Keep chilled", icon: Snowflake },
  ambient: { label: "Room temp", icon: Sun },
  dry: { label: "Keep dry", icon: Sun },
};

const SPICE = ["None", "Mild", "Medium", "Hot"];

const isOutOfStock = (item: any) =>
  item?.available === false || (item?.stock ?? 0) <= 0;

const discountOf = (item: any): number | null => {
  const was = item?.compareAtPrice ?? 0;
  const now = item?.rate ?? 0;
  if (!(was > now) || was <= 0) return null;
  return Math.round(((was - now) / was) * 100);
};

/** Newest listings carry a NEW flag for a week; best sellers come from reviews. */
const isNew = (item: any) => {
  const t = Date.parse(item?.createdAt ?? "");
  if (Number.isNaN(t)) return false;
  return Date.now() - t < 7 * 86_400_000;
};

type SortKey = "default" | "freshest" | "price_asc" | "price_desc" | "name_asc";

/* ═══════════════════════════════════════════════════════════
   LOCATION TIERS
═══════════════════════════════════════════════════════════ */
const COUNTRY_TO_CONTINENT: Record<string, string> = {
  estonia: "Europe", latvia: "Europe", lithuania: "Europe", finland: "Europe",
  sweden: "Europe", norway: "Europe", denmark: "Europe", germany: "Europe",
  france: "Europe", spain: "Europe", italy: "Europe", portugal: "Europe",
  poland: "Europe", "united kingdom": "Europe", ireland: "Europe",
  netherlands: "Europe", belgium: "Europe", austria: "Europe",
  switzerland: "Europe", greece: "Europe",
  "united states": "North America", canada: "North America", mexico: "North America",
  brazil: "South America", argentina: "South America",
  nigeria: "Africa", kenya: "Africa", "south africa": "Africa",
  china: "Asia", japan: "Asia", india: "Asia", "united arab emirates": "Asia",
  australia: "Oceania", "new zealand": "Oceania",
};
const getContinent = (country?: string | null): string | null => {
  if (!country) return null;
  return COUNTRY_TO_CONTINENT[country.toLowerCase().trim()] ?? null;
};
const isLocalTier = (tier: number) => tier <= 1;
const isGlobalTier = (tier: number) => tier >= 2;

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS — prefixed gs- so this page can be mounted next to
   FreshStoreHome and StoreProductsHome without clashing.
═══════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Archivo+Black&display=swap');
.gs-page *, .gs-page *::before, .gs-page *::after { box-sizing: border-box; }
.gs-page ::-webkit-scrollbar { display: none; }
.gs-page input, .gs-page button, .gs-page select { font-family: inherit; }

@keyframes gsPulse { 0%,100%{opacity:1} 50%{opacity:.35} }
@keyframes gsRise { from{opacity:0;transform:translateY(10px);} to{opacity:1;transform:none;} }

.gs-fade { animation: gsRise .24s ease both; }

.gs-rail { display:flex; align-items:flex-start; gap:22px; overflow-x:auto; scrollbar-width:none; -webkit-overflow-scrolling:touch; }
.gs-railbtn { background:none; border:none; cursor:pointer; flex-shrink:0; display:flex; flex-direction:column; align-items:center; gap:7px; padding:2px; width:78px; }
.gs-railic { width:46px; height:46px; border-radius:14px; display:flex; align-items:center; justify-content:center; font-size:20px; transition:transform .18s, box-shadow .18s, background .18s; overflow:hidden; }
.gs-railbtn:hover .gs-railic { transform:translateY(-3px); box-shadow:0 8px 18px rgba(21,30,18,.12); }
.gs-raillabel { font-size:10.5px; font-weight:700; line-height:1.2; text-align:center; }

.gs-card { background:#fff; border:1px solid ${G.border}; border-radius:16px; transition:box-shadow .2s, transform .2s, border-color .2s; }
.gs-card:hover { box-shadow:0 14px 34px rgba(21,30,18,.10); transform:translateY(-3px); border-color:${G.greenSoft}; }

.gs-add { border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; border-radius:50%; transition:transform .14s, box-shadow .18s; }
.gs-add:hover { transform:scale(1.08); }
.gs-add:active { transform:scale(.92); }

.gs-arrow { width:34px; height:34px; border-radius:50%; border:1px solid ${G.border}; background:#fff; display:flex; align-items:center; justify-content:center; cursor:pointer; box-shadow:0 4px 14px rgba(21,30,18,.10); }
.gs-arrow:hover { border-color:${G.green}; }

.gs-chip { display:inline-flex; align-items:center; gap:5px; font-size:10.5px; font-weight:700; padding:4px 9px; border-radius:999px; }

.gs-tab { display:flex; flex-direction:column; align-items:center; gap:3px; font-size:10px; font-weight:700; cursor:pointer; color:${G.inkFaint}; padding:4px 10px; }
.gs-tab.active { color:${G.green}; }

.gs-treerow { display:flex; align-items:center; gap:10px; font-weight:600; font-size:13.5px; color:${G.ink}; cursor:pointer; padding:9px 10px; border-radius:11px; }
.gs-treerow:hover { background:${G.greenSoft}; }
.gs-treerow.on { background:${G.greenSoft}; color:${G.greenDark}; font-weight:800; }

.gs-storecard { display:flex; flex-direction:column; gap:9px; background:#fff; border:1px solid ${G.border}; border-radius:16px; padding:14px; cursor:pointer; transition:border-color .18s, box-shadow .18s, transform .18s; }
.gs-storecard:hover { border-color:${G.green}; box-shadow:0 12px 28px rgba(21,30,18,.10); transform:translateY(-2px); }

@media (prefers-reduced-motion: reduce){
  .gs-fade, .gs-card, .gs-add, .gs-railic { animation:none !important; transition:none !important; }
}
`;

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const GroceryStoreHome: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);
  const cur = symbol ?? "€";
  const fx = (n: number) => (n ?? 0) * (rate ?? 1);
  const price = (n: number) => `${cur}${fx(n).toFixed(2)}`;

  const authUser = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
  const userId = authUser?.userId ?? "";

  const listings = useSelector((s: RootState) => (s as any).food.listings as FoodItem[]);
  const filteredListings = useSelector((s: RootState) => (s as any).food.filteredListings as FoodItem[]);
  const cartItems = useSelector((s: RootState) => s.cart.items);
  const ratingSummaries = useSelector((s: RootState) => (s as any).productRatings?.summaries ?? {});
  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);

  const cartTotal = cartItems.reduce((s: number, i: any) => s + (i.qty ?? 1), 0);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [catOpen, setCatOpen] = useState(false);
  const [desktopCatOpen, setDesktopCatOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSort, setActiveSort] = useState<SortKey>("default");

  /* aisle selections inside Grocery */
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [activeDeep, setActiveDeep] = useState<string | null>(null);
  const [openNodes, setOpenNodes] = useState<Set<string>>(new Set());
  const [popularExpanded, setPopularExpanded] = useState(true);

  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [userCity, setUserCity] = useState<string | null>(null);
  const [userLat, setUserLat] = useState<number | null>(null);
  const [locationDenied, setLocationDenied] = useState(false);
  const [locationLoading, setLocationLoading] = useState(true);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [showGlobal, setShowGlobal] = useState(false);
  const locationActive = !locationDenied && userLat !== null;

  /* detail sheet state */
  const [detailItem, setDetailItem] = useState<any | null>(null);
  const [imgIndex, setImgIndex] = useState(0);
  const [qty, setQty] = useState(1);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);

  const railRef = useRef<HTMLDivElement>(null);
  const popularRef = useRef<HTMLDivElement>(null);

  const brandMap = useMemo(() => {
    const m = new Map<string, any>();
    (commercePartners || []).forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [commercePartners]);

  const brandTier = useMemo(() => {
    const tiers = new Map<string, number>();
    const deviceCity = userCity?.toLowerCase().trim();
    const deviceCountry = userCountry?.toLowerCase().trim();
    const deviceContinent = getContinent(userCountry);
    (commercePartners || []).forEach((p: any) => {
      const brandCity = p.address?.city?.toLowerCase().trim();
      const brandCountry = p.address?.country?.toLowerCase().trim();
      const brandContinent = getContinent(p.address?.country);
      let tier = 3;
      if (deviceCity && brandCity && brandCity === deviceCity) tier = 0;
      else if (deviceCountry && brandCountry && brandCountry === deviceCountry) tier = 1;
      else if (deviceContinent && brandContinent && brandContinent === deviceContinent) tier = 2;
      tiers.set(p.brandId, tier);
    });
    return tiers;
  }, [commercePartners, userCity, userCountry]);

  /* ── responsive ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  /* ── auth gate ── */
  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  /* ── data listeners ── */
  useEffect(() => { dispatch(listenToPublishedFoods() as any); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllProductSummaries() as any); }, [dispatch]);
  useEffect(() => {
    const unsub = dispatch(listenToBrandsByBusinessType(GROCERY_BUSINESS_TYPE) as any);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [dispatch]);
  useEffect(() => { dispatch(filterLocalFoodsByCountry(null) as any); }, [dispatch]);

  /* ── geolocation ── */
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true); setLocationLoading(false);
      dispatch(filterLocalFoodsByCountry(null) as any);
      return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLat(pos.coords.latitude); setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } }, (results: any, status: string) => {
              if (status === "OK" && results?.[0]) {
                const comps = results[0].address_components;
                const get = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                setUserCity(get("locality") || get("administrative_area_level_2"));
                const country = get("country"); setUserCountry(country);
                if (country) dispatch(filterLocalFoodsByCountry(country) as any);
              }
              setLocationLoading(false);
            });
          } else { setLocationLoading(false); }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          setLocationLoading(false);
          dispatch(filterLocalFoodsByCountry(null) as any);
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName }).then((r) => {
        r.onchange = () => { if (r.state === "granted") detect(); };
      }).catch(() => {});
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalFoodsByCountry(showGlobal ? null : userCountry) as any);
  }, [userCountry, showGlobal, dispatch]);

  /* ── deals countdown: today's prices hold until midnight ── */
  const [countdown, setCountdown] = useState("00 : 00 : 00");
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const end = new Date(now); end.setHours(23, 59, 59, 999);
      let s = Math.max(0, Math.floor((end.getTime() - now.getTime()) / 1000));
      const h = Math.floor(s / 3600); s -= h * 3600;
      const m = Math.floor(s / 60); s -= m * 60;
      const pad = (n: number) => String(n).padStart(2, "0");
      setCountdown(`${pad(h)} : ${pad(m)} : ${pad(s)}`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  /* ── actions ── */
  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    setActiveCat(null); setActiveDeep(null);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalFoodsByCountry(next ? null : userCountry) as any);
  };

  const resetAllFilters = () => {
    setActiveCat(null); setActiveDeep(null); setActiveSort("default"); setSearchQuery("");
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalFoodsByCountry(showGlobal ? null : userCountry) as any);
  };

  const handleCatSelect = (id: string | null) => {
    setActiveCat((p) => (id === null || p === id ? null : id));
    setActiveDeep(null);
  };
  const handleDeepSelect = (id: string) => setActiveDeep((p) => (p === id ? null : id));

  const toggleNode = (id: string) =>
    setOpenNodes((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty ?? 0;

  /* One basket, many stores — grocery runs are rarely one shop. */
  const SINGLE_BRAND_CART_MODE = false;
  const ensureCartBrandCompat = (newBrandId?: string) => {
    if (!SINGLE_BRAND_CART_MODE || !cartItems.length) return;
    const currentBrandId = cartItems[0]?.brandId;
    if (currentBrandId && newBrandId && currentBrandId !== newBrandId) dispatch(clearCart());
  };

  /**
   * One place builds the cart line, so the shelf, the sheet and the till
   * never disagree about what's being bought or what it costs.
   */
  const buildFoodCartLine = (item: any, sizeId: string | null, extraIds: string[], quantity: number) => {
    const rt: RateType = (item?.rateType as RateType) || DEFAULT_RATE_TYPE;
    const unit = computeItemPrice(item, sizeId, extraIds);
    return {
      listingId: buildCartId(item, sizeId, extraIds),
      baseListingId: item.listingId,
      name: buildCartLabel(item, sizeId, extraIds),
      rate: unit,
      rateType: rt,
      qty: quantity,
      lineTotal: calculateLineTotal(unit, quantity, rt),
      photo: item.photos?.[0],
      hostId: item.hostId,
      brandId: item.brandId,
      unit: item.unit,
      netContent: item.netContent,
      portionId: sizeId ?? undefined,
      extraIds: extraIds.length ? extraIds : undefined,
      categoryId: item.categoryId ?? "",
      attributes: item.attributes ?? {},
    };
  };

  const addLine = (item: any, sizeId: string | null, extraIds: string[], count = 1) => {
    if (isOutOfStock(item)) return;
    ensureCartBrandCompat(item.brandId);
    dispatch(addToCart(buildFoodCartLine(item, sizeId, extraIds, Math.max(1, count)) as any));
  };

  const removeLine = (item: any, sizeId: string | null, extraIds: string[]) =>
    dispatch(decrementCartItem({ listingId: buildCartId(item, sizeId, extraIds) }));

  const goToCheckout = (extra?: any) =>
    navigate(`/Freshpay/${userId}`, { state: { items: extra ? [...cartItems, extra] : cartItems } });
  const goToBrand = (id: string) => { if (id && id !== "unknown") navigate(`/FreshStorebrand/${id}`); };

  const openDetail = (item: any) => {
    setDetailItem(item);
    setImgIndex(0);
    setQty(1);
    setSelectedExtras([]);
    const sizes = portionsOf(item);
    setSelectedSize(sizes.length ? sizes[0].id : null);
  };
  const closeDetail = () => setDetailItem(null);

  /* quick add from a card — pack sizes or extras send you to the sheet */
  const quickAdd = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isOutOfStock(item)) return;
    if (portionsOf(item).length || extrasOf(item).length) { openDetail(item); return; }
    addLine(item, null, [], 1);
  };

  /* React 19 types useRef<T>(null) as RefObject<T | null>, so accept the nullable ref. */
  const scrollRail = (ref: React.RefObject<HTMLDivElement | null>, dir: 1 | -1) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.8), behavior: "smooth" });
  };

  /* ═══════════════════════════════════
     FILTERING — everything resolved through the ancestry chain
     of the item's saved categoryId, and always inside Grocery.
  ═══════════════════════════════════ */
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list: any[] = (filteredListings ?? []).filter((l: any) => {
      if (!l.isPublished) return false;
      const chain = chainIdsOf(l.categoryId);
      if (!chain.includes(FOOD_DEPT_ID)) return false;
      if (!chain.includes(GROCERY_BRANCH_ID)) return false;
      if (activeCat && !chain.includes(activeCat)) return false;
      if (activeDeep && !chain.includes(activeDeep)) return false;
      if (q) {
        const hay = `${l.name ?? ""} ${l.description ?? ""} ${l.category ?? ""} ${l.unit ?? ""} ${(l.allergens ?? []).join(" ")} ${(l.tags ?? []).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    if (activeSort === "price_asc") list = [...list].sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0));
    else if (activeSort === "price_desc") list = [...list].sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0));
    else if (activeSort === "name_asc") list = [...list].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    else if (activeSort === "freshest")
      list = [...list].sort((a, b) => {
        const av = Date.parse(a.harvestedOn ?? a.bestBefore ?? "") || 0;
        const bv = Date.parse(b.harvestedOn ?? b.bestBefore ?? "") || 0;
        return bv - av;
      });

    return list;
  }, [filteredListings, activeCat, activeDeep, searchQuery, activeSort]);

  const productsByBrand = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, any[]>();
    filteredProducts.forEach((item: any) => {
      const bId = item.brandId || item.hostId || "unknown";
      if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
      map.get(bId)!.push(item);
    });
    let groups = order.map((bId) => ({ brandId: bId, items: map.get(bId)! }));

    if (locationActive) {
      groups = groups.filter((g) => {
        const tier = brandTier.get(g.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    return [...groups].sort((a, b) => (brandTier.get(a.brandId) ?? 3) - (brandTier.get(b.brandId) ?? 3));
  }, [filteredProducts, showGlobal, brandTier, locationActive]);

  const visibleCount = useMemo(
    () => productsByBrand.reduce((sum, g) => sum + g.items.length, 0),
    [productsByBrand]
  );

  const aisles = useMemo(() => childrenOf(GROCERY_BRANCH_ID), []);
  const activeFilterName = activeDeep ? catName(activeDeep) : activeCat ? catName(activeCat) : null;

  /* deals — biggest price cuts on the shelf right now */
  const deals = useMemo(() => {
    return [...(listings ?? [])]
      .filter((l: any) => l.isPublished && !isOutOfStock(l) && isGrocery(l.categoryId) && discountOf(l))
      .sort((a: any, b: any) => (discountOf(b) ?? 0) - (discountOf(a) ?? 0))
      .slice(0, 12);
  }, [listings]);

  /* the stores that actually stock this aisle near you */
  const nearbyStores = useMemo(() => {
    const stocked = new Set<string>();
    (listings ?? []).forEach((l: any) => {
      if (l.isPublished && isGrocery(l.categoryId)) stocked.add(l.brandId || l.hostId);
    });
    let list = (commercePartners || []).filter((p: any) => stocked.has(p.brandId));
    if (locationActive) {
      list = list.filter((p: any) => {
        const tier = brandTier.get(p.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    return [...list]
      .sort((a: any, b: any) => (brandTier.get(a.brandId) ?? 3) - (brandTier.get(b.brandId) ?? 3))
      .slice(0, 10);
  }, [commercePartners, listings, brandTier, locationActive, showGlobal]);

  /* popular categories — big photo tiles under the aisle you're in */
  const popularTiles = useMemo(() => {
    const source = activeCat ? childrenOf(activeCat) : aisles;
    return source.slice(0, 14);
  }, [activeCat, aisles]);

  /* ═══════════════════════════════════
     HEADER  (plain function calls, not <Component />, so the
     search input keeps focus between keystrokes)
  ═══════════════════════════════════ */
  const brandMark = (size = 30) => (
    <button
      onClick={() => { resetAllFilters(); if (userId) navigate(`/GroceryStoreHome/${userId}`); }}
      style={{ display: "flex", alignItems: "center", gap: 9, background: "none", border: "none", cursor: "pointer", padding: 0, flexShrink: 0 }}
      aria-label="Heartlas Grocery home"
    >
      <img src={appLogo} alt="Heartlas" style={{ width: size, height: size, objectFit: "contain" }} />
      <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1 }}>
        <span style={{ fontFamily: "'Archivo Black','Montserrat',sans-serif", fontSize: size * 0.62, color: G.red, letterSpacing: "-0.02em" }}>
          heartlas
        </span>
        <span style={{ fontSize: size * 0.29, fontWeight: 800, letterSpacing: "0.42em", color: G.inkSoft, marginTop: 3 }}>
          GROCERY
        </span>
      </span>
    </button>
  );

  const searchField = () => (
    <div style={{
      display: "flex", alignItems: "center", flex: 1, minWidth: 0, background: "#fff",
      border: `1.5px solid ${G.border}`, borderRadius: 999, padding: "5px 6px 5px 18px", gap: 8,
    }}>
      <input
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search groceries, brands or stores..."
        aria-label="Search groceries"
        style={{ flex: 1, minWidth: 0, border: "none", outline: "none", fontSize: 13.5, color: G.ink, background: "transparent" }}
      />
      {searchQuery && (
        <button onClick={() => setSearchQuery("")} aria-label="Clear search"
          style={{ background: "none", border: "none", cursor: "pointer", color: G.inkFaint, display: "flex" }}>
          <X size={14} />
        </button>
      )}
      <button aria-label="Search"
        style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: G.red, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
        <Search size={16} color="#fff" />
      </button>
    </div>
  );

  const headerAction = (icon: React.ReactNode, label: string, action: () => void, badge?: number) => (
    <button onClick={action} aria-label={label}
      style={{ background: "none", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, color: G.ink, position: "relative", padding: "2px 4px" }}>
      {icon}
      <span style={{ fontSize: 10.5, fontWeight: 700 }}>{label}</span>
      {badge ? (
        <span style={{ position: "absolute", top: -5, right: -2, background: G.red, color: "#fff", fontSize: 9, fontWeight: 900, minWidth: 17, height: 17, padding: "0 4px", borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {badge > 99 ? "99+" : badge}
        </span>
      ) : null}
    </button>
  );

  const deliverToButton = () => (
    <button
      onClick={locationDenied ? () => setLocationModalOpen(true) : handleToggleGlobal}
      style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", flexShrink: 0, textAlign: "left" }}
    >
      {locationLoading
        ? <span style={{ width: 8, height: 8, borderRadius: "50%", background: G.green, animation: "gsPulse 1.2s infinite" }} />
        : locationDenied ? <AlertTriangle size={17} color={G.gold} /> : showGlobal ? <Globe size={17} color={G.green} /> : <MapPin size={17} color={G.green} />}
      <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
        <span style={{ fontSize: 10.5, fontWeight: 600, color: G.inkFaint }}>Deliver to</span>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: G.ink }}>
          {locationLoading ? "Finding you…" : locationDenied ? "Set location" : showGlobal ? "Everywhere" : userCity && userCountry ? `${userCity}, ${userCountry}` : userCountry || "Nearby"}
        </span>
      </span>
    </button>
  );

  const headerBar = () => (
    <header style={{
      position: "sticky", top: 0, zIndex: 200, background: "#fff",
      borderBottom: `1px solid ${G.divider}`, boxShadow: "0 2px 14px rgba(21,30,18,.05)",
    }}>
      {isMobile ? (
        <div style={{ padding: "10px 14px 12px", display: "flex", flexDirection: "column", gap: 11 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {brandMark(26)}
            <div style={{ flex: 1 }} />
            <button onClick={() => setCatOpen(true)} aria-label="Browse categories"
              style={{ background: "none", border: "none", cursor: "pointer", color: (activeCat || activeDeep) ? G.green : G.ink, display: "flex", position: "relative" }}>
              <LayoutGrid size={20} />
              {(activeCat || activeDeep) && (
                <span style={{ position: "absolute", top: -1, right: -1, width: 7, height: 7, borderRadius: "50%", background: G.green, border: "1.5px solid #fff" }} />
              )}
            </button>
            <button onClick={locationDenied ? () => setLocationModalOpen(true) : handleToggleGlobal} aria-label="Delivery location"
              style={{ background: "none", border: "none", cursor: "pointer", color: G.ink, display: "flex" }}>
              {showGlobal ? <Globe size={20} /> : <MapPin size={20} />}
            </button>
            <button onClick={() => goToCheckout()} aria-label="Basket"
              style={{ background: "none", border: "none", cursor: "pointer", color: G.ink, display: "flex", position: "relative" }}>
              <ShoppingCart size={20} />
              {cartTotal > 0 && (
                <span style={{ position: "absolute", top: -6, right: -7, background: G.red, color: "#fff", fontSize: 9, fontWeight: 900, minWidth: 16, height: 16, padding: "0 4px", borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {cartTotal > 99 ? "99+" : cartTotal}
                </span>
              )}
            </button>
          </div>
          {searchField()}
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 22, padding: "14px 26px" }}>
          {brandMark(32)}
          {deliverToButton()}
          <div style={{ flex: 1, display: "flex", maxWidth: 620 }}>{searchField()}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
            {headerAction(<BadgePercent size={19} />, "Offers", () => { setActiveSort("price_asc"); setActiveCat(null); })}
            {headerAction(<ShoppingBag size={19} />, "Orders", () => navigate(`/FreshOrders/${userId}`))}
            {headerAction(<User size={19} />, "Account", () => navigate(`/profile/${userId}`))}
            {headerAction(<ShoppingCart size={19} />, "Cart", () => goToCheckout(), cartTotal)}
          </div>
        </div>
      )}
    </header>
  );

  /* ═══════════════════════════════════
     AISLE RAIL — All Categories + every aisle under Grocery
  ═══════════════════════════════════ */
  const aisleRail = () => (
    <div style={{
      position: "relative", background: "#fff", border: `1px solid ${G.border}`,
      borderRadius: 16, padding: isMobile ? "12px 12px" : "14px 18px", marginBottom: 18,
    }}>
      <div ref={railRef} className="gs-rail">
        <button className="gs-railbtn" onClick={() => { handleCatSelect(null); closePanel(); }}>
          <span className="gs-railic" style={{
            background: activeCat === null ? GRAD_GREEN : G.greenSoft,
            color: activeCat === null ? "#fff" : G.greenDark,
          }}>
            <LayoutGrid size={20} />
          </span>
          <span className="gs-raillabel" style={{ color: activeCat === null ? G.greenDark : G.inkSoft, fontWeight: activeCat === null ? 800 : 700 }}>
            All Categories
          </span>
        </button>

        {aisles.map((node) => {
          const on = activeCat === node.id;
          return (
            <button key={node.id} className="gs-railbtn" onClick={() => handleCatSelect(node.id)}>
              <span className="gs-railic" style={{
                background: on ? GRAD_GREEN : G.bg,
                color: on ? "#fff" : G.green,
                border: `1px solid ${on ? "transparent" : G.border}`,
              }}>
                {(node as any).image
                  ? <img src={(node as any).image} alt={node.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : node.icon}
              </span>
              <span className="gs-raillabel" style={{ color: on ? G.greenDark : G.inkSoft, fontWeight: on ? 800 : 700 }}>
                {node.name}
              </span>
            </button>
          );
        })}
      </div>

      {!isMobile && (
        <button className="gs-arrow" onClick={() => scrollRail(railRef, 1)} aria-label="More categories"
          style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }}>
          <ChevronRight size={17} color={G.ink} />
        </button>
      )}
    </div>
  );

  /* ═══════════════════════════════════
     HERO — the promise, the promo, the two delivery facts
  ═══════════════════════════════════ */
  const heroBlock = () => (
    <div style={{
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1.9fr 1fr",
      gap: isMobile ? 12 : 16,
      marginBottom: 26,
    }}>
      {/* banner */}
      <div style={{
        position: "relative", borderRadius: 18, overflow: "hidden",
        minHeight: isMobile ? 190 : 210, background: G.greenDeep,
        display: "flex", alignItems: "center",
      }}>
        {HERO_IMAGE && (
          <img src={HERO_IMAGE} alt=""
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        )}
        <div style={{ position: "absolute", inset: 0, background: GRAD_HERO }} />

        <div style={{ position: "relative", padding: isMobile ? "20px 18px" : "30px 34px", maxWidth: 460 }}>
          <h1 style={{
            fontFamily: "'Archivo Black','Montserrat',sans-serif", color: "#fff", margin: 0,
            fontSize: isMobile ? 25 : 36, lineHeight: 1.08, letterSpacing: "-0.02em",
          }}>
            Fresh groceries,<br />delivered to you
          </h1>
          <p style={{ color: "rgba(255,255,255,.86)", fontSize: isMobile ? 11.5 : 13, fontWeight: 600, margin: "12px 0 0" }}>
            Top quality • Best prices • Fast delivery
          </p>
          <button
            onClick={() => { setActiveSort("price_asc"); setActiveCat(null); setActiveDeep(null); }}
            style={{
              marginTop: 18, display: "inline-flex", alignItems: "center", gap: 8,
              background: "#fff", color: G.ink, border: "none", borderRadius: 999,
              padding: isMobile ? "10px 18px" : "12px 22px", fontSize: 12.5, fontWeight: 800, cursor: "pointer",
              boxShadow: "0 8px 22px rgba(0,0,0,.22)",
            }}>
            {isMobile ? "Shop Now" : "Shop All Offers"} <ChevronRight size={15} />
          </button>
        </div>

        {!isMobile && (
          <div style={{
            position: "absolute", top: 26, right: 34, width: 96, height: 96, borderRadius: "50%",
            background: G.gold, color: G.ink, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", boxShadow: "0 12px 30px rgba(0,0,0,.28)",
          }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".08em" }}>UP TO</span>
            <span style={{ fontFamily: "'Archivo Black',sans-serif", fontSize: 26, lineHeight: 1 }}>30%</span>
            <span style={{ fontSize: 10.5, fontWeight: 800 }}>OFF</span>
          </div>
        )}
      </div>

      {/* delivery facts */}
      <div style={{ display: "grid", gap: isMobile ? 10 : 16, gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr" }}>
        <div style={{
          background: "#fff", border: `1px solid ${G.border}`, borderRadius: 16, padding: 16,
          display: "flex", alignItems: "center", gap: 12, overflow: "hidden",
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: G.ink }}>Fast Delivery</p>
            <p style={{ margin: "6px 0 0", fontSize: 11.5, color: G.inkSoft, lineHeight: 1.5 }}>
              Get your order in <strong style={{ color: G.ink }}>15–30 min</strong>
            </p>
            <p style={{ margin: "8px 0 0", fontSize: 11, color: G.inkFaint }}>From local stores near you</p>
          </div>
          {COURIER_IMAGE
            ? <img src={COURIER_IMAGE} alt="" style={{ width: 92, height: 62, objectFit: "contain", flexShrink: 0 }} />
            : <Truck size={34} color={G.green} style={{ flexShrink: 0 }} />}
        </div>

        <div style={{
          background: "#fff", border: `1px solid ${G.border}`, borderRadius: 16, padding: 16,
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: G.ink }}>Free Delivery</p>
            <p style={{ margin: "6px 0 0", fontSize: 11.5, color: G.inkSoft }}>On orders over {price(30)}</p>
          </div>
          <span style={{ width: 44, height: 44, borderRadius: 13, background: G.redSoft, color: G.red, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Truck size={22} />
          </span>
        </div>
      </div>
    </div>
  );

  /* ═══════════════════════════════════
     DEAL CARD + PRODUCT CARD
  ═══════════════════════════════════ */
  const dietDots = (item: any) => {
    const dots: React.ReactNode[] = [];
    const dot = (key: string, title: string, bg: string, color: string, node: React.ReactNode) => (
      <span key={key} title={title} style={{ width: 18, height: 18, borderRadius: 6, background: bg, color, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
        {node}
      </span>
    );
    if (item.isVegan) dots.push(dot("vg", "Vegan", G.greenSoft, G.green, <Leaf size={10} />));
    else if (item.isVegetarian) dots.push(dot("v", "Vegetarian", G.greenSoft, G.green, <Sparkles size={10} />));
    if (item.isGlutenFree) dots.push(dot("gf", "Gluten-free", G.goldSoft, G.gold, <WheatOff size={10} />));
    if ((item.spiceLevel ?? 0) > 0) dots.push(dot("sp", SPICE[item.spiceLevel] || "Spicy", G.redSoft, G.red, <Flame size={10} />));
    return dots;
  };

  const cardFlag = (item: any) => {
    const d = discountOf(item);
    const summary = ratingSummaries[item.listingId];
    if (isOutOfStock(item)) return { text: "SOLD OUT", bg: G.inkFaint, color: "#fff" };
    if (d) return { text: `-${d}%`, bg: G.red, color: "#fff" };
    if (summary?.totalReviews >= 25 && summary.avgRating >= 4.5) return { text: "BEST SELLER", bg: G.gold, color: G.ink };
    if (isNew(item)) return { text: "NEW", bg: G.green, color: "#fff" };
    return null;
  };

  const productCard = (item: any, opts?: { compact?: boolean }) => {
    const soldOut = isOutOfStock(item);
    const configurable = portionsOf(item).length > 0 || extrasOf(item).length > 0;
    const q = configurable || soldOut ? 0 : getQty(item.listingId);
    const accent = brandAccentColor(brandMap.get(item.brandId));
    const flag = cardFlag(item);
    const measure = measureLabel(item);
    const fresh = freshnessChip(item);
    const summary = ratingSummaries[item.listingId];
    const was = item.compareAtPrice ?? 0;

    return (
      <div
        key={item.listingId}
        className="gs-card"
        onClick={() => openDetail(item)}
        style={{
          padding: 12, cursor: "pointer", position: "relative", display: "flex", flexDirection: "column",
          width: opts?.compact ? (isMobile ? 148 : 176) : undefined,
          flexShrink: opts?.compact ? 0 : undefined,
          opacity: soldOut ? 0.6 : 1,
        }}
      >
        {flag && (
          <span className="gs-chip" style={{ position: "absolute", top: 10, left: 10, zIndex: 2, background: flag.bg, color: flag.color, borderRadius: 7, letterSpacing: ".03em" }}>
            {flag.text}
          </span>
        )}
        {item.isOrganic && !flag && (
          <span className="gs-chip" style={{ position: "absolute", top: 10, left: 10, zIndex: 2, background: G.greenSoft, color: G.green }}>
            <Leaf size={10} /> Organic
          </span>
        )}

        <div style={{
          width: "100%", aspectRatio: "1/1", borderRadius: 12, overflow: "hidden",
          background: G.bg, marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {item.photos?.[0]
            ? <img src={item.photos[0]} alt={item.name}
                style={{ width: "100%", height: "100%", objectFit: "cover", filter: soldOut ? "grayscale(1)" : "none" }}
                onError={(e) => {
                  const el = e.currentTarget as HTMLImageElement;
                  el.src = appLogo; el.style.objectFit = "contain"; el.style.opacity = "0.35";
                }} />
            : <Package size={30} color={G.green} style={{ opacity: .35 }} />}
        </div>

        <div style={{ fontSize: 13, fontWeight: 700, color: G.ink, lineHeight: 1.3, marginBottom: 3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", minHeight: 34 }}>
          {item.name}
        </div>

        <div style={{ fontSize: 11, color: G.inkFaint, marginBottom: 8, display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
          <span>{measure || catName(item.categoryId) || "1 pc"}</span>
          {summary?.totalReviews ? <span>· {summary.avgRating.toFixed(1)}★</span> : null}
        </div>

        {!opts?.compact && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 8, minHeight: 18, flexWrap: "wrap" }}>
            {dietDots(item)}
            {!soldOut && fresh && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 9.5, fontWeight: 800, color: fresh.color }}>
                <Clock size={9} /> {fresh.text}
              </span>
            )}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: "auto" }}>
          <span style={{ minWidth: 0 }}>
            <span style={{ fontSize: 14.5, fontWeight: 900, color: discountOf(item) ? G.red : G.ink }}>
              {configurable ? "From " : ""}{price(item.rate ?? 0)}
            </span>
            {was > (item.rate ?? 0) && (
              <span style={{ fontSize: 11, color: G.inkFaint, textDecoration: "line-through", marginLeft: 6 }}>
                {price(was)}
              </span>
            )}
          </span>

          {soldOut ? (
            <span style={{ fontSize: 10, fontWeight: 800, color: G.inkFaint }}>Unavailable</span>
          ) : q > 0 ? (
            <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 6, background: G.greenSoft, borderRadius: 999, padding: "3px 5px" }}>
              <button className="gs-add" aria-label="Remove one"
                onClick={(e) => { e.stopPropagation(); removeLine(item, null, []); }}
                style={{ width: 22, height: 22, background: accent, color: "#fff" }}>
                <Minus size={12} />
              </button>
              <span style={{ fontSize: 12, fontWeight: 800, color: G.ink, minWidth: 12, textAlign: "center" }}>{q}</span>
              <button className="gs-add" aria-label="Add one" onClick={(e) => quickAdd(item, e)}
                style={{ width: 22, height: 22, background: accent, color: "#fff" }}>
                <Plus size={13} />
              </button>
            </div>
          ) : (
            <button className="gs-add"
              aria-label={configurable ? `Choose options for ${item.name}` : `Add ${item.name} to basket`}
              onClick={(e) => quickAdd(item, e)}
              style={{ width: 30, height: 30, background: accent, boxShadow: `0 4px 12px ${accent}55`, flexShrink: 0 }}>
              <Plus size={16} color="#fff" />
            </button>
          )}
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════
     FRESH DEALS NEAR YOU
  ═══════════════════════════════════ */
  const dealsBlock = () => (
    <section style={{ marginBottom: 30 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 19, fontWeight: 900, color: G.ink, letterSpacing: "-0.02em" }}>
          Fresh Deals Near You
        </h2>
        {deals.length > 0 && (
          <span className="gs-chip" style={{ background: G.redSoft, color: G.red }}>
            <Clock size={11} /> Ends in {countdown}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={() => { setActiveSort("price_asc"); setActiveCat(null); setActiveDeep(null); }}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 800, color: G.green }}>
          View all deals
        </button>
      </div>

      {deals.length === 0 ? (
        <div style={{ background: "#fff", border: `1px dashed ${G.border}`, borderRadius: 16, padding: 26, textAlign: "center", color: G.inkSoft, fontSize: 12.5 }}>
          No price cuts on the shelf right now. Deals land here the moment a store marks one down.
        </div>
      ) : (
        <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4, scrollbarWidth: "none" as any }}>
          {deals.map((item: any) => productCard(item, { compact: true }))}
        </div>
      )}
    </section>
  );

  /* ═══════════════════════════════════
     NEARBY GROCERY STORES
  ═══════════════════════════════════ */
  const storesBlock = () => (
    <section style={{ marginBottom: 30 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 19, fontWeight: 900, color: G.ink, letterSpacing: "-0.02em" }}>
          Nearby Grocery Stores
        </h2>
        <div style={{ flex: 1 }} />
        <button onClick={() => navigate(`/FreshStores/${userId}`)}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 800, color: G.green }}>
          View all stores
        </button>
      </div>

      {nearbyStores.length === 0 ? (
        <div style={{ background: "#fff", border: `1px dashed ${G.border}`, borderRadius: 16, padding: 26, textAlign: "center", color: G.inkSoft, fontSize: 12.5 }}>
          {locationDenied
            ? "Turn on location and we'll show the stores that deliver to your door."
            : `No ${showGlobal ? "global" : "local"} stores stocking this aisle yet.`}
        </div>
      ) : (
        <div style={{
          display: isMobile ? "flex" : "grid",
          gridTemplateColumns: isMobile ? undefined : "repeat(auto-fill,minmax(210px,1fr))",
          gap: 12, overflowX: isMobile ? "auto" : undefined, paddingBottom: isMobile ? 4 : 0,
        }}>
          {nearbyStores.map((p: any) => {
            const summaryRating = p.rating ?? p.avgRating;
            const reviews = p.totalReviews ?? p.reviewCount;
            const eta = p.deliveryTime || p.eta || "20–30 min";
            const freeOver = p.freeDeliveryOver ?? 30;
            return (
              <div key={p.brandId} className="gs-storecard" onClick={() => goToBrand(p.brandId)}
                style={{ width: isMobile ? 210 : undefined, flexShrink: isMobile ? 0 : undefined }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 11, overflow: "hidden", border: `1px solid ${G.border}`, background: "#fff", flexShrink: 0 }}>
                    <img src={p.image || appLogo} alt={p.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      onError={(e) => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 800, color: G.ink, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.name || "Store"}
                  </span>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: G.inkSoft, fontWeight: 600, flexWrap: "wrap" }}>
                  {summaryRating ? (
                    <span style={{ color: G.gold, fontWeight: 800 }}>
                      ★ {Number(summaryRating).toFixed(1)}
                      {reviews ? <span style={{ color: G.inkFaint, fontWeight: 600 }}> ({reviews})</span> : null}
                    </span>
                  ) : null}
                  <span>Delivery {eta}</span>
                </div>

                <span className="gs-chip" style={{ background: G.greenSoft, color: G.greenDark, alignSelf: "flex-start" }}>
                  <Truck size={11} /> Free delivery over {price(freeOver)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );

  /* ═══════════════════════════════════
     POPULAR CATEGORIES — photo tiles, select not navigate
  ═══════════════════════════════════ */
  const popularBlock = () => {
    if (!popularTiles.length) return null;
    return (
      <section style={{ marginBottom: 30 }}>
        <div onClick={() => setPopularExpanded((v) => !v)}
          style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none", marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 19, fontWeight: 900, color: G.ink, letterSpacing: "-0.02em" }}>
            {activeCat ? `Popular in ${catName(activeCat)}` : "Popular Categories"}
          </h2>
          <motion.div animate={{ rotate: popularExpanded ? 0 : -90 }} transition={{ duration: 0.2 }}>
            <ChevronDown size={19} color={G.inkSoft} />
          </motion.div>
          <div style={{ flex: 1 }} />
          {!isMobile && (
            <div style={{ display: "flex", gap: 8 }}>
              <button className="gs-arrow" onClick={(e) => { e.stopPropagation(); scrollRail(popularRef, -1); }} aria-label="Scroll left">
                <ChevronLeft size={16} color={G.ink} />
              </button>
              <button className="gs-arrow" onClick={(e) => { e.stopPropagation(); scrollRail(popularRef, 1); }} aria-label="Scroll right">
                <ChevronRight size={16} color={G.ink} />
              </button>
            </div>
          )}
        </div>

        <AnimatePresence initial={false}>
          {popularExpanded && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.24 }} style={{ overflow: "hidden" }}>
              <div ref={popularRef} style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4, scrollbarWidth: "none" as any }}>
                {popularTiles.map((node) => {
                  const on = activeDeep === node.id || activeCat === node.id;
                  return (
                    <button key={node.id}
                      onClick={() => (activeCat ? handleDeepSelect(node.id) : handleCatSelect(node.id))}
                      style={{
                        position: "relative", flexShrink: 0, width: isMobile ? 150 : 196, height: isMobile ? 108 : 128,
                        borderRadius: 16, overflow: "hidden", border: "none", padding: 0, cursor: "pointer",
                        background: G.greenDeep, outline: on ? `2.5px solid ${G.green}` : "none", outlineOffset: 2,
                      }}>
                      {(node as any).image
                        ? <img src={(node as any).image} alt={node.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        : (
                          <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40, color: "rgba(255,255,255,.35)" }}>
                            {node.icon}
                          </span>
                        )}
                      <span style={{ position: "absolute", inset: 0, background: `linear-gradient(to top, rgba(14,74,36,.92), transparent 62%)` }} />
                      <span style={{ position: "absolute", left: 12, bottom: 10, right: 12, textAlign: "left", color: "#fff", fontSize: isMobile ? 12.5 : 14, fontWeight: 800, textShadow: "0 1px 6px rgba(0,0,0,.4)" }}>
                        {node.name}
                      </span>
                      {on && (
                        <span style={{ position: "absolute", top: 8, right: 8, width: 20, height: 20, borderRadius: "50%", background: G.green, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Check size={12} color="#fff" strokeWidth={3} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    );
  };

  /* ═══════════════════════════════════
     SIDEBAR TREE / MOBILE SHEET
  ═══════════════════════════════════ */
  const TreeNode: React.FC<{ node: CategoryItem; depth: number }> = ({ node, depth }) => {
    const kids = childrenOf(node.id);
    const isOpen = openNodes.has(node.id);
    const picked = activeCat === node.id || activeDeep === node.id;

    return (
      <div style={{ paddingLeft: depth === 0 ? 0 : 12 }}>
        <div className={`gs-treerow${picked ? " on" : ""}`}
          onClick={() => {
            if (depth === 0) handleCatSelect(node.id);
            else handleDeepSelect(node.id);
            if (kids.length) toggleNode(node.id);
            else closePanel();
          }}>
          <span style={{ display: "flex", color: picked ? G.green : G.inkSoft }}>{node.icon}</span>
          <span style={{ flex: 1, minWidth: 0 }}>{node.name}</span>
          {kids.length > 0 ? (
            <motion.div animate={{ rotate: isOpen ? 90 : 0 }}>
              <ChevronRight size={14} color={G.inkFaint} />
            </motion.div>
          ) : picked ? <Check size={14} color={G.green} /> : null}
        </div>
        <AnimatePresence>
          {isOpen && kids.length > 0 && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
              {kids.map((k) => <TreeNode key={k.id} node={k} depth={depth + 1} />)}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const categoryTree = () => (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".14em", color: G.inkFaint }}>AISLES</span>
        <div style={{ flex: 1 }} />
        {(activeCat || activeDeep) && (
          <button onClick={resetAllFilters}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11.5, fontWeight: 800, color: G.green }}>
            Clear
          </button>
        )}
      </div>
      <div className={`gs-treerow${!activeCat && !activeDeep ? " on" : ""}`} onClick={() => { handleCatSelect(null); closePanel(); }}>
        <LayoutGrid size={15} />
        <span style={{ flex: 1 }}>All groceries</span>
      </div>
      {aisles.map((node) => <TreeNode key={node.id} node={node} depth={0} />)}
    </div>
  );

  const mobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(21,30,18,.5)", zIndex: 400 }} />
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", borderRadius: "22px 22px 0 0", background: "#fff", zIndex: 401, overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: G.border, borderRadius: 2, margin: "14px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${G.divider}`, padding: "12px 20px 10px" }}>
              <span style={{ fontWeight: 800, fontSize: 11, letterSpacing: ".15em", color: G.green }}>CATEGORIES</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} aria-label="Close categories"
                style={{ background: "none", border: "none", cursor: "pointer" }}>
                <X size={16} color={G.inkSoft} />
              </button>
            </div>
            <div style={{ padding: "14px 14px 32px" }}>{categoryTree()}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     STORE ROW HEADER + PRODUCT ROWS
  ═══════════════════════════════════ */
  const brandRowHeader = (brandId: string) => {
    const partner = brandMap.get(brandId);
    const shopName = partner?.name || "Store";
    const shopLogo = partner?.image || appLogo;
    const city = partner?.address?.city;
    const accent = brandAccentColor(partner);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "24px 0 12px" }}>
        <button onClick={() => goToBrand(brandId)} aria-label={`Open ${shopName}`}
          style={{ width: 34, height: 34, borderRadius: 10, overflow: "hidden", flexShrink: 0, border: `1px solid ${G.border}`, background: "#fff", padding: 0, cursor: "pointer" }}>
          <img src={shopLogo} alt={shopName} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
        </button>
        <span onClick={() => goToBrand(brandId)}
          style={{ fontWeight: 900, fontSize: 14, color: G.ink, cursor: "pointer", textDecoration: "underline", textDecorationColor: accent, textDecorationThickness: 2, textUnderlineOffset: 4 }}>
          {shopName}
        </span>
        {city && (
          <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 700, color: G.inkFaint }}>
            <MapPin size={10} /> {city}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <motion.button whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }} onClick={() => goToBrand(brandId)}
          aria-label={`Shop ${shopName}`}
          style={{ width: 30, height: 30, borderRadius: "50%", border: `1px solid ${G.border}`, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <ChevronRight size={16} color={accent} />
        </motion.button>
      </div>
    );
  };

  const emptyState = (title: string, body: string, cta: string, action: () => void) => (
    <div style={{ textAlign: "center", padding: 54, background: "#fff", border: `1px solid ${G.border}`, borderRadius: 18 }}>
      <div style={{ width: 54, height: 54, borderRadius: 16, background: G.greenSoft, color: G.green, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
        <Package size={24} />
      </div>
      <h3 style={{ fontWeight: 900, fontSize: 19, color: G.ink, margin: "0 0 8px" }}>{title}</h3>
      <p style={{ color: G.inkSoft, margin: "0 0 20px", fontSize: 13 }}>{body}</p>
      <button onClick={action}
        style={{ padding: "12px 26px", borderRadius: 999, border: "none", background: GRAD_GREEN, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>
        {cta}
      </button>
    </div>
  );

  const productsBlock = () => (
    <section style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ width: 4, height: 20, borderRadius: 2, background: GRAD_GREEN }} />
          <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 19, fontWeight: 900, color: G.ink, letterSpacing: "-0.02em" }}>
            {activeFilterName ?? (locationDenied ? "All groceries" : showGlobal ? "Global shelves" : userCity ? `Near ${userCity}` : userCountry ?? "All groceries")}
          </h2>
          <span className="gs-chip" style={{ background: G.greenSoft, color: G.greenDark }}>
            {visibleCount} items · {productsByBrand.length} stores
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <select value={activeSort} onChange={(e) => setActiveSort(e.target.value as SortKey)} aria-label="Sort groceries"
            style={{ border: `1px solid ${G.border}`, borderRadius: 999, padding: "6px 12px", fontSize: 11.5, fontWeight: 700, color: G.inkSoft, background: "#fff", cursor: "pointer", outline: "none" }}>
            <option value="default">Featured</option>
            <option value="freshest">Freshest first</option>
            <option value="price_asc">Price: low → high</option>
            <option value="price_desc">Price: high → low</option>
            <option value="name_asc">Name A–Z</option>
          </select>
          {locationActive && (
            <button onClick={handleToggleGlobal}
              style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? "#fff" : G.greenSoft, borderRadius: 999, padding: "6px 13px", border: `1px solid ${showGlobal ? G.border : "transparent"}`, fontSize: 11.5, fontWeight: 800, color: showGlobal ? G.inkSoft : G.greenDark, cursor: "pointer" }}>
              {showGlobal ? <MapPin size={11} /> : <Globe size={11} />}{showGlobal ? "Show local" : "Show global"}
            </button>
          )}
        </div>
      </div>

      {productsByBrand.length === 0 ? (
        filteredProducts.length === 0 ? (
          activeFilterName
            ? emptyState(`No ${activeFilterName} in stock`, "Pick another aisle, or clear the filters to see the whole shop.", "Clear filters", resetAllFilters)
            : emptyState("No groceries listed yet", "Stores publish their shelves here — check back shortly.", "Clear filters", resetAllFilters)
        ) : locationDenied ? (
          emptyState("Turn on location", "We use it to show stores that can actually deliver to you.", "Enable location", () => setLocationModalOpen(true))
        ) : (
          emptyState(
            showGlobal ? "No global groceries right now" : "No local groceries right now",
            `Switch to ${showGlobal ? "local" : "global"} above, or clear your filters.`,
            "Clear filters",
            resetAllFilters
          )
        )
      ) : (
        productsByBrand.map((group) => (
          <div key={group.brandId}>
            {brandRowHeader(group.brandId)}
            <div style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(auto-fill,minmax(200px,1fr))",
              gap: isMobile ? 12 : 16,
            }}>
              {group.items.map((item: any) => productCard(item))}
            </div>
          </div>
        ))
      )}
    </section>
  );

  /* ═══════════════════════════════════
     PRODUCT DETAIL — sheet on mobile, modal on desktop
  ═══════════════════════════════════ */
  const detailModal = () => {
    if (!detailItem) return null;

    const item = detailItem;
    const photos: string[] = item.photos?.length ? item.photos : [];
    const summary = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;

    const sizes = portionsOf(item);
    const extras = extrasOf(item);

    const detailRows = savedAttrsOf(item)
      .filter(({ def }) => def.inputType !== "color")
      .map(({ def, raw }) => ({ name: def.name, value: resolveAttrValue(def, raw) }))
      .filter((r) => r.value);

    const stock = item.stock ?? 0;
    const soldOut = isOutOfStock(item);
    const cartId = buildCartId(item, selectedSize, selectedExtras);
    const inCart = cartItems.find((i: any) => i.listingId === cartId)?.qty ?? 0;
    const rt: RateType = (item?.rateType as RateType) || DEFAULT_RATE_TYPE;
    const rawUnit = computeItemPrice(item, selectedSize, selectedExtras);
    const lineTotal = `${cur}${fx(calculateLineTotal(rawUnit, qty, rt)).toFixed(2)}`;

    const fresh = freshnessChip(item);
    const measure = measureLabel(item);
    const storage = STORAGE_META[String(item.storage ?? "").toLowerCase()];
    const StorageIcon = storage?.icon ?? Package;
    const allergens: string[] = Array.isArray(item.allergens) ? item.allergens : [];
    const discount = discountOf(item);
    const partner = brandMap.get(item.brandId);

    const needsSize = sizes.length > 0 && !selectedSize;
    const canAdd = !soldOut && !needsSize;

    const closeBtn = (
      <button onClick={closeDetail} aria-label="Close"
        style={{ background: "rgba(255,255,255,.94)", border: "none", width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(21,30,18,.2)" }}>
        <X size={15} color={G.ink} />
      </button>
    );

    const chip = (key: string, bg: string, color: string, icon: React.ReactNode, text: string) => (
      <span key={key} className="gs-chip" style={{ background: bg, color, fontSize: 11, padding: "5px 10px" }}>
        {icon} {text}
      </span>
    );

    const content = (
      <div style={{ background: "#fff", borderRadius: isMobile ? "24px 24px 0 0" : 20, overflow: "hidden", maxWidth: isMobile ? "100%" : 470, width: "100%", maxHeight: isMobile ? "88vh" : "84vh", display: "flex", flexDirection: "column" }}>
        <div style={{ height: 4, background: GRAD_GREEN, flexShrink: 0 }} />
        {isMobile && <div style={{ width: 36, height: 4, borderRadius: 2, background: G.border, margin: "12px auto 0", flexShrink: 0 }} />}

        <div style={{ overflowY: "auto", padding: "18px 20px 0" }}>
          {photos.length > 0 ? (
            <div style={{ position: "relative", width: "100%", aspectRatio: "16/10", borderRadius: 14, overflow: "hidden", marginBottom: 16, background: G.bg }}>
              <img src={photos[imgIndex]} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              {discount && (
                <div className="gs-chip" style={{ position: "absolute", top: 10, left: 10, background: G.red, color: "#fff" }}>-{discount}%</div>
              )}
              <div style={{ position: "absolute", top: 10, right: 10 }}>{closeBtn}</div>
              {photos.length > 1 && (
                <div style={{ position: "absolute", bottom: 10, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 5 }}>
                  {photos.map((_, i) => (
                    <button key={i} aria-label={`Photo ${i + 1}`} onClick={() => setImgIndex(i)}
                      style={{ width: i === imgIndex ? 16 : 6, height: 6, borderRadius: 3, border: "none", padding: 0, cursor: "pointer", background: i === imgIndex ? G.green : "rgba(255,255,255,.8)" }} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>{closeBtn}</div>
          )}

          {partner?.name && (
            <p style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: brandAccentColor(partner), margin: "0 0 5px", cursor: "pointer" }}
              onClick={() => { closeDetail(); goToBrand(item.brandId); }}>
              {partner.name}
            </p>
          )}

          {item.categoryId && (
            <p style={{ fontSize: 10.5, fontWeight: 700, color: G.inkFaint, margin: "0 0 6px", display: "flex", alignItems: "center", gap: 5 }}>
              <Tag size={10} /> {catPathLabel(item.categoryId)}
            </p>
          )}

          <div style={{ fontSize: 16.5, fontWeight: 800, color: G.ink, marginBottom: 8 }}>{item.name}</div>

          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <span style={{ fontSize: 21, fontWeight: 900, color: discount ? G.red : G.ink }}>{price(rawUnit)}</span>
            {discount && (
              <span style={{ fontSize: 13, color: G.inkFaint, textDecoration: "line-through" }}>{price(item.compareAtPrice ?? 0)}</span>
            )}
            {measure && <span style={{ fontSize: 12, fontWeight: 800, color: G.inkSoft }}>{measure}</span>}
            {hasRating && (
              <span style={{ fontSize: 12, fontWeight: 700, color: G.inkSoft }}>
                {summary.avgRating.toFixed(1)}★ ({summary.totalReviews.toLocaleString()})
              </span>
            )}
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
            {item.isOrganic && chip("org", G.greenSoft, G.green, <Leaf size={11} />, "Organic")}
            {item.isVegan && chip("vg", G.greenSoft, G.green, <Leaf size={11} />, "Vegan")}
            {!item.isVegan && item.isVegetarian && chip("v", G.greenSoft, G.green, <Sparkles size={11} />, "Vegetarian")}
            {item.isGlutenFree && chip("gf", G.goldSoft, G.gold, <WheatOff size={11} />, "Gluten-free")}
            {(item.spiceLevel ?? 0) > 0 && chip("sp", G.redSoft, G.red, <Flame size={11} />, SPICE[item.spiceLevel] || "Spicy")}
            {fresh && chip("fr", `${fresh.color}1A`, fresh.color, <Clock size={11} />, fresh.text)}
            {storage && chip("st", G.goldSoft, G.gold, <StorageIcon size={11} />, storage.label)}
          </div>

          {/* ── PACK SIZES — one choice ── */}
          {sizes.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".18em", color: G.inkFaint, textTransform: "uppercase" }}>Pack size</span>
                {needsSize && <span style={{ fontSize: 10, fontWeight: 700, color: G.red }}>Please select</span>}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {sizes.map((s) => {
                  const on = selectedSize === s.id;
                  return (
                    <button key={s.id} onClick={() => setSelectedSize(s.id)}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "11px 14px", borderRadius: 12, border: `1.5px solid ${on ? G.green : G.border}`, background: on ? G.greenSoft : "#fff", cursor: "pointer" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${on ? G.green : G.inkFaint}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {on && <span style={{ width: 9, height: 9, borderRadius: "50%", background: G.green }} />}
                        </span>
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: G.ink }}>
                          {s.name}{s.unit ? ` · ${s.unit}` : ""}
                        </span>
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: on ? G.greenDark : G.inkSoft }}>
                        {s.price > 0 ? `+${price(s.price)}` : "Included"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── EXTRAS — many ── */}
          {extras.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".18em", color: G.inkFaint, marginBottom: 10, textTransform: "uppercase" }}>Add extras</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {extras.map((ex) => {
                  const on = selectedExtras.includes(ex.id);
                  return (
                    <button key={ex.id}
                      onClick={() => setSelectedExtras((s) => (on ? s.filter((id) => id !== ex.id) : [...s, ex.id]))}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "11px 14px", borderRadius: 12, border: `1.5px solid ${on ? G.green : G.border}`, background: on ? G.greenSoft : "#fff", cursor: "pointer" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ width: 18, height: 18, borderRadius: 6, border: `2px solid ${on ? G.green : G.inkFaint}`, background: on ? G.green : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {on && <Check size={12} color="#fff" />}
                        </span>
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: G.ink }}>{ex.name}</span>
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: on ? G.greenDark : G.inkSoft }}>+{price(ex.price)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {item.description && (
            <div style={{ fontSize: 13, color: G.inkSoft, lineHeight: 1.65, marginBottom: 16 }}
              dangerouslySetInnerHTML={{ __html: item.description }} />
          )}

          {/* ── what the store filled in ── */}
          {(() => {
            const rows: [string, string][] = detailRows.map((d) => [d.name, d.value]);
            if (item.calories) rows.push(["Calories", `${item.calories} kcal`]);
            if (item.harvestedOn) rows.push(["Harvested", new Date(item.harvestedOn).toLocaleDateString()]);
            if (item.bestBefore) rows.push(["Best before", new Date(item.bestBefore).toLocaleDateString()]);
            if (item.batchCode) rows.push(["Batch", item.batchCode]);
            if (allergens.length) rows.push(["Allergens", allergens.join(", ")]);
            if (!rows.length) return null;
            return (
              <div style={{ border: `1px solid ${G.border}`, borderRadius: 14, overflow: "hidden", marginBottom: 16 }}>
                {rows.map(([k, v], i) => (
                  <div key={`${k}-${i}`} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "9px 13px", background: i % 2 ? "#fff" : G.bg, fontSize: 12 }}>
                    <span style={{ color: G.inkFaint, fontWeight: 700 }}>{k}</span>
                    <span style={{ color: G.ink, fontWeight: 700, textAlign: "right" }}>{v}</span>
                  </div>
                ))}
              </div>
            );
          })()}

          {soldOut ? (
            <p style={{ fontSize: 11.5, fontWeight: 800, color: G.inkFaint, marginBottom: 12 }}>Sold out for now</p>
          ) : stock <= 5 ? (
            <p style={{ fontSize: 11.5, fontWeight: 800, color: G.red, marginBottom: 12 }}>Only {stock} left</p>
          ) : null}

          {!soldOut && (
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".16em", color: G.inkFaint }}>QTY</span>
              <div style={{ display: "flex", border: `1.5px solid ${G.border}`, borderRadius: 12, overflow: "hidden", background: G.bg }}>
                <button aria-label="Decrease quantity" onClick={() => setQty((v) => Math.max(1, v - 1))}
                  style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: "pointer", color: G.green, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Minus size={14} />
                </button>
                <div style={{ width: 44, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: G.ink, borderLeft: `1px solid ${G.border}`, borderRight: `1px solid ${G.border}` }}>{qty}</div>
                <button aria-label="Increase quantity" onClick={() => setQty((v) => Math.min(stock || 99, v + 1))} disabled={qty >= (stock || 99)}
                  style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: qty >= (stock || 99) ? "not-allowed" : "pointer", color: G.green, opacity: qty >= (stock || 99) ? 0.4 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Plus size={14} />
                </button>
              </div>
              {inCart > 0 && (
                <button onClick={() => removeLine(item, selectedSize, selectedExtras)}
                  style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", fontSize: 11.5, fontWeight: 800, color: G.red }}>
                  {inCart} in basket — remove one
                </button>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: "14px 20px 20px", display: "flex", gap: 10, flexShrink: 0, borderTop: `1px solid ${G.divider}` }}>
          <button disabled={!canAdd}
            onClick={() => { addLine(item, selectedSize, selectedExtras, qty); closeDetail(); }}
            style={{ flex: 1, padding: "13px 0", borderRadius: 12, border: `1.5px solid ${G.ink}`, background: "transparent", color: G.ink, fontWeight: 700, fontSize: 13, cursor: canAdd ? "pointer" : "not-allowed", opacity: canAdd ? 1 : 0.45 }}>
            {soldOut ? "Sold out" : needsSize ? "Pick a pack size" : "Add to basket"}
          </button>
          <button disabled={!canAdd}
            onClick={() => {
              const line = buildFoodCartLine(item, selectedSize, selectedExtras, qty);
              addLine(item, selectedSize, selectedExtras, qty);
              closeDetail();
              goToCheckout(line);
            }}
            style={{ flex: 1.25, padding: "13px 0", borderRadius: 12, border: "none", background: canAdd ? GRAD_GREEN : "#C9C9C9", color: "#fff", fontWeight: 800, fontSize: 13, cursor: canAdd ? "pointer" : "not-allowed" }}>
            Checkout · {lineTotal}
          </button>
        </div>
      </div>
    );

    return createPortal(
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeDetail}
          style={{ position: "fixed", inset: 0, background: "rgba(21,30,18,.66)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", zIndex: 500 }}>
          {isMobile ? (
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
              onClick={(e) => e.stopPropagation()} style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 501 }}>
              {content}
            </motion.div>
          ) : (
            <div style={{ position: "fixed", inset: 0, zIndex: 501, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={(e) => e.stopPropagation()}>
              <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 10 }}
                transition={{ type: "spring", damping: 26, stiffness: 340 }}
                style={{ width: 470, maxWidth: "100%", borderRadius: 20, overflow: "hidden", boxShadow: "0 32px 80px rgba(21,30,18,.35)" }}>
                {content}
              </motion.div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  };

  /* ═══════════════════════════════════
     LOCATION MODAL · TRUST STRIP · BOTTOM TABS
  ═══════════════════════════════════ */
  const locationModal = () => (
    <AnimatePresence>
      {locationModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLocationModalOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(21,30,18,.78)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <motion.div initial={{ scale: 0.9, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 40 }} onClick={(e) => e.stopPropagation()}
            style={{ width: 320, borderRadius: 20, padding: 24, background: "#fff", boxShadow: "0 30px 80px rgba(21,30,18,.5)", textAlign: "center" }}>
            <div style={{ height: 5, borderRadius: 999, background: GRAD_GREEN, marginBottom: 20 }} />
            <div style={{ width: 52, height: 52, borderRadius: 16, background: G.greenSoft, color: G.green, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
              <MapPin size={24} />
            </div>
            <h3 style={{ fontWeight: 900, margin: "0 0 8px", color: G.ink }}>Location is off</h3>
            <p style={{ color: G.inkSoft, margin: "0 0 22px", fontSize: 13, lineHeight: 1.6 }}>
              Allow location in your browser settings to see the stores that deliver to you.
            </p>
            <button onClick={() => setLocationModalOpen(false)}
              style={{ padding: "11px 28px", borderRadius: 999, border: "none", background: GRAD_GREEN, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 12.5 }}>
              Got it
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const trustStrip = () => (
    <div style={{
      display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10,
      background: "#fff", border: `1px solid ${G.border}`, borderRadius: 16,
      padding: "12px 10px", marginBottom: 22,
    }}>
      {[
        { icon: <Truck size={15} />, title: "Fast Delivery", body: "15–30 min" },
        { icon: <BadgePercent size={15} />, title: "Free Delivery", body: `Over ${price(30)}` },
        { icon: <ShieldCheck size={15} />, title: "Secure Payment", body: "100% safe" },
      ].map((t) => (
        <div key={t.title} style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ color: G.green, display: "flex", flexShrink: 0 }}>{t.icon}</span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 10.5, fontWeight: 800, color: G.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.title}</span>
            <span style={{ display: "block", fontSize: 9.5, color: G.inkFaint, fontWeight: 600 }}>{t.body}</span>
          </span>
        </div>
      ))}
    </div>
  );

  

  /* mobile floating basket — the till is never more than one tap away */
  const mobileBasketBar = () => (isMobile && cartTotal > 0 && !detailItem ? (
    <button onClick={() => goToCheckout()} className="gs-fade"
      style={{
        position: "fixed", left: 14, right: 14, bottom: 76, zIndex: 120,
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        padding: "13px 18px", borderRadius: 14, border: "none", cursor: "pointer",
        background: GRAD_GREEN, color: "#fff", boxShadow: "0 14px 30px rgba(18,92,44,.4)",
      }}>
      <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 800 }}>
        <ShoppingCart size={16} /> {cartTotal} {cartTotal === 1 ? "item" : "items"}
      </span>
      <span style={{ fontSize: 13, fontWeight: 900 }}>Checkout</span>
    </button>
  ) : null);

  /* ═══════════════════════════════════
     RENDER
  ═══════════════════════════════════ */
  const mainColumn = () => (
    <>
      {aisleRail()}
      {heroBlock()}
      {isMobile && trustStrip()}
      {dealsBlock()}
      {storesBlock()}
      {popularBlock()}
      {productsBlock()}
    </>
  );

  return (
    <div className="gs-page" style={{
      minHeight: "100vh", background: G.bg, paddingBottom: isMobile ? 150 : 40,
      fontFamily: "'Montserrat',sans-serif", color: G.ink,
    }}>
      <style>{GLOBAL_CSS}</style>

      {headerBar()}
      {mobileCategorySheet()}

      {!isMobile ? (
        <div style={{
          display: "grid", gap: 22, padding: "22px 26px 0",
          gridTemplateColumns: desktopCatOpen ? "268px 1fr" : "1fr",
          maxWidth: 1440, margin: "0 auto",
        }}>
          {desktopCatOpen && (
            <aside style={{
              background: "#fff", border: `1px solid ${G.border}`, borderRadius: 18, padding: 16,
              position: "sticky", top: 96, maxHeight: "calc(100vh - 120px)", overflowY: "auto",
            }}>
              {categoryTree()}
            </aside>
          )}
          <main style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <button onClick={() => setDesktopCatOpen((v) => !v)}
                style={{ display: "flex", alignItems: "center", gap: 7, background: "#fff", border: `1px solid ${G.border}`, borderRadius: 999, padding: "8px 14px", fontSize: 12, fontWeight: 800, color: G.ink, cursor: "pointer" }}>
                <LayoutGrid size={14} color={G.green} /> {desktopCatOpen ? "Hide aisles" : "Browse aisles"}
              </button>
              {activeFilterName && (
                <span className="gs-chip" style={{ background: G.greenSoft, color: G.greenDark, fontSize: 11.5, padding: "6px 12px" }}>
                  {activeFilterName}
                  <button onClick={resetAllFilters} aria-label="Clear filter"
                    style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", display: "flex", padding: 0, marginLeft: 2 }}>
                    <X size={11} />
                  </button>
                </span>
              )}
              <div style={{ flex: 1 }} />
            </div>
            {mainColumn()}
          </main>
        </div>
      ) : (
        <div style={{ padding: "14px 14px 0" }}>{mainColumn()}</div>
      )}

      {detailModal()}
      {locationModal()}
      {mobileBasketBar()}
   
    </div>
  );
};

export default GroceryStoreHome;